import React, { useState, useEffect } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put, Post, DeleteSingle } from '@app/utils/httpProvider';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import {
    acceptDoc,
    acceptImage,
    messageError,
    messageToast,
    status200,
} from '../../utils/define';
import {
    ASSET_API, ASSETGROUP_API, DEPARTMENT_API, ASSETSTATUS_API
} from '../../utils/apiUrl.js';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import InputFile from '@app/components/input-file/InputFile';
import AccessoryTab from './components/AccessoryTab';
import MaintenanceEventTab from './components/MaintenanceEventTab';
import AccessoryHistoryTab from './components/AccessoryHistoryTab';

const safeDateFormat = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
        return moment(d).format('YYYY-MM-DD');
    }
    return '';
};

const schema = yup.object().shape({
    assetCode: yup.string().required(messageError.emptyMessage),
    assetName: yup.string().required(messageError.emptyMessage),
    assetGroupId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn hoặc nhập Id hợp lệ'),
    currentDepartmentId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn hoặc nhập Id hợp lệ'),
    assetStatusId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn hoặc nhập Id hợp lệ'),
    serialNumber: yup.string().nullable(),
    model: yup.string().nullable(),
    manufacturer: yup.string().nullable(),
    country: yup.string().nullable(),
    powerSpec: yup.string().nullable(),
    purchaseDate: yup.string().nullable(),
    purchaseValue: yup.number().nullable().min(0, 'Giá trị mua không được nhỏ hơn 0'),
});

const AssetEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const paramUrl = new URLSearchParams(location.search.substring(1));
    const itemId = paramUrl.get('id');

    const itemEdit = useQuery([itemId], () =>
        Get(ASSET_API.GET_ITEM, itemId)
    );

    const assetGroups = useQuery(['getAllAssetGroupsForMapping'], () =>
        Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const departments = useQuery(['getAllDepartmentsForMapping'], () =>
        Get(DEPARTMENT_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const assetStatuses = useQuery(['getAllAssetStatusesForMapping'], () =>
        Get(ASSETSTATUS_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const departmentOptions = departments?.data?.data?.map(item => ({ value: item.id, label: item.departmentName })) || [];
    const assetGroupOptions = assetGroups?.data?.data?.map(item => ({ value: item.id, label: item.assetGroupName })) || [];
    const assetStatusOptions = assetStatuses?.data?.data?.map(item => ({ value: item.id, label: item.statusName })) || [];

    const editFunc = useMutation((value) =>
        Put(ASSET_API.UPDATE_ITEM, itemId, value)
    );

    const handleEdit = async (values) => {
        const model = { ...values };

        model.id = itemId;
        // Convert to numbers
        model.assetGroupId = Number(model.assetGroupId);
        model.currentDepartmentId = Number(model.currentDepartmentId);
        model.assetStatusId = Number(model.assetStatusId);
        model.purchaseValue = model.purchaseValue ? Number(model.purchaseValue) : null;

        editFunc.mutate(model, {
            onSuccess: async (res) => {
                if (res.status < 200 || res.status >= 300) {
                    let errorMessage = messageToast.messageToastUpdateError;
                    try {
                        const errData = await res.json();
                        if (errData?.message) {
                            errorMessage = errData.message;
                        } else if (errData?.errors) {
                            errorMessage = Object.values(errData.errors).flat().join(', ');
                        }
                    } catch (e) {
                        console.error("Error parsing response JSON:", e);
                    }
                    toast.error(errorMessage);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(itemId, model);
                    history.goBack();
                }
            },
            onError: (err) => {
                let errorMessage = 'Đã có lỗi xảy ra, vui lòng thử lại !';
                if (err?.message) {
                    errorMessage = err.message;
                } else if (err?.errors) {
                    errorMessage = Object.values(err.errors).flat().join(', ');
                }
                toast.error(errorMessage);
            }
        });
    };

    const isLoading = itemEdit.isLoading;
    const isError = itemEdit.isError;
    const error = itemEdit.error;

    return (
        <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex align-items-center py-3">
                <h5 className="card-title fw-bold text-primary mb-0">
                    <i className="fas fa-edit mr-2" />
                    Chỉnh sửa tài sản
                </h5>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div>Đang tải...</div>
                ) : isError ? (
                    <div>Có lỗi xảy ra: ${error.message}</div>
                ) : itemEdit.data == null ? (
                    <div>Thẻ tài sản không tồn tại</div>
                ) : (
                    <Formik
                        enableReinitialize
                        validationSchema={schema}
                        onSubmit={(values) => handleEdit(values)}
                        initialValues={{
                            assetCode: itemEdit.data?.assetCode || '',
                            assetName: itemEdit.data?.assetName || '',
                            assetGroupId: itemEdit.data?.assetGroupId || 0,
                            currentDepartmentId: itemEdit.data?.currentDepartmentId || 0,
                            assetStatusId: itemEdit.data?.assetStatusId || 0,
                            serialNumber: itemEdit.data?.serialNumber || '',
                            model: itemEdit.data?.model || '',
                            manufacturer: itemEdit.data?.manufacturer || '',
                            country: itemEdit.data?.country || '',
                            powerSpec: itemEdit.data?.powerSpec || '',
                            purchaseDate: itemEdit.data?.purchaseDate ? safeDateFormat(itemEdit.data.purchaseDate) : '',
                            purchaseValue: itemEdit.data?.purchaseValue || 0,
                            isDisposed: itemEdit.data?.isDisposed || false,
                                    imagePath: itemEdit.data?.imagePath || '',
                                    imageThumbnail: itemEdit.data?.imageThumbnail || '',
                                    documentLink: itemEdit.data?.documentLink || []
                                }}
                        >
                        {({
                            handleSubmit,
                            handleChange,
                            values,
                            errors,
                            setFieldValue
                        }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Tabs>
                                    <TabList>
                                        <Tab>Thông tin tài sản</Tab>
                                        <Tab>Hình ảnh & Tài liệu</Tab>
                                        <Tab>Danh sách phụ kiện</Tab>
                                        <Tab>Lịch sử điều chuyển</Tab>
                                        <Tab>Lịch sử sửa chữa</Tab>
                                    </TabList>

                                    {/* Tab 1: Thông tin tài sản */}
                                    <TabPanel>
                                        <Row>
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-3 text-primary">Thông tin chung</h6>
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Mã tài sản',
                                                            name: 'assetCode',
                                                            value: values.assetCode,
                                                            onChange: handleChange,
                                                            isInvalid: errors.assetCode,
                                                            error: errors.assetCode
                                                        },
                                                        {
                                                            label: 'Tên tài sản',
                                                            name: 'assetName',
                                                            value: values.assetName,
                                                            onChange: handleChange,
                                                            isInvalid: errors.assetName,
                                                            error: errors.assetName
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Nhóm TS',
                                                            name: 'assetGroupId',
                                                            component: (
                                                                <Select
                                                                    options={assetGroupOptions}
                                                                    value={assetGroupOptions.find(opt => opt.value === values.assetGroupId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('assetGroupId', selectedOption ? selectedOption.value : 0)}
                                                                    placeholder="-- Chọn nhóm tài sản --"
                                                                    isClearable={true}
                                                                    className={errors.assetGroupId ? 'is-invalid' : ''}
                                                                />
                                                            ),
                                                            error: errors.assetGroupId
                                                        },
                                                        {
                                                            label: 'Phòng ban',
                                                            name: 'currentDepartmentId',
                                                            component: (
                                                                <Select
                                                                    options={departmentOptions}
                                                                    value={departmentOptions.find(opt => opt.value === values.currentDepartmentId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('currentDepartmentId', selectedOption ? selectedOption.value : 0)}
                                                                    placeholder="-- Chọn phòng ban --"
                                                                    isClearable={true}
                                                                    className={errors.currentDepartmentId ? 'is-invalid' : ''}
                                                                />
                                                            ),
                                                            error: errors.currentDepartmentId
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Số Serial',
                                                            name: 'serialNumber',
                                                            value: values.serialNumber,
                                                            onChange: handleChange,
                                                            isInvalid: errors.serialNumber,
                                                            error: errors.serialNumber
                                                        },
                                                        {
                                                            label: 'Model / Ký hiệu',
                                                            name: 'model',
                                                            value: values.model,
                                                            onChange: handleChange,
                                                            isInvalid: errors.model,
                                                            error: errors.model
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Nhà sản xuất',
                                                            name: 'manufacturer',
                                                            value: values.manufacturer,
                                                            onChange: handleChange,
                                                            isInvalid: errors.manufacturer,
                                                            error: errors.manufacturer
                                                        },
                                                        {
                                                            label: 'Xuất xứ',
                                                            name: 'country',
                                                            value: values.country,
                                                            onChange: handleChange,
                                                            isInvalid: errors.country,
                                                            error: errors.country
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Công suất',
                                                            name: 'powerSpec',
                                                            value: values.powerSpec,
                                                            onChange: handleChange,
                                                            isInvalid: errors.powerSpec,
                                                            error: errors.powerSpec
                                                        },
                                                        {
                                                            label: "Ngày mua",
                                                            name: 'purchaseDate',
                                                            component: (
                                                                <DatePicker
                                                                    wrapperClassName="w-100"
                                                                    className={`form-control ${errors.purchaseDate ? 'is-invalid' : ''}`}
                                                                    selected={values.purchaseDate && moment(values.purchaseDate, 'YYYY-MM-DD').isValid() ? moment(values.purchaseDate, 'YYYY-MM-DD').toDate() : null}
                                                                    onChange={(date) => setFieldValue('purchaseDate', date ? moment(date).format('YYYY-MM-DD') : '')}
                                                                    dateFormat="dd/MM/yyyy"
                                                                    isClearable
                                                                    placeholderText="dd/MM/yyyy"
                                                                />
                                                            ),
                                                            error: errors.purchaseDate
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Giá trị mua (VND)',
                                                            name: 'purchaseValue',
                                                            type: 'number',
                                                            min: 0,
                                                            value: values.purchaseValue,
                                                            onChange: handleChange,
                                                            isInvalid: errors.purchaseValue,
                                                            error: errors.purchaseValue
                                                        },
                                                        null // Empty right slot
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                                        {
                                                            label: 'Trạng thái',
                                                            name: 'assetStatusId',
                                                            component: (
                                                                <Select
                                                                    options={assetStatusOptions}
                                                                    value={assetStatusOptions.find(opt => opt.value === values.assetStatusId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('assetStatusId', selectedOption ? selectedOption.value : 0)}
                                                                    placeholder="-- Chọn trạng thái --"
                                                                    isClearable={true}
                                                                    isDisabled={true}
                                                                    // placeholder="-- Trạng thái được cập nhật tự động --"
                                                                    // isClearable={false}
                                                                    // isDisabled={true}
                                                                    className={errors.assetStatusId ? 'is-invalid' : ''}
                                                                />
                                                            ),
                                                            error: errors.assetStatusId
                                                        },
                                                        {
                                                            label: 'Tài sản đã thanh lý',
                                                            name: 'isDisposed',
                                                            component: (
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    label="Đánh dấu tài sản đã được thanh lý"
                                                                    name="isDisposed"
                                                                    checked={values.isDisposed}
                                                                    onChange={handleChange}
                                                                />
                                                            )
                                                        }
                                                    ]}
                                                />
                                            </Col>
                                        </Row>
                                        <hr />
                                        <div className="d-flex justify-content-end mt-2">
                                            <Button
                                                type="submit"
                                                theme="primary"
                                                disabled={isLoading}
                                                className="mr-2"
                                            >
                                                <i className="fas fa-save mr-1" /> Lưu cập nhật
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={() => history.goBack()}
                                                theme="secondary"
                                                className="mr-2"
                                            >
                                                <i className="fas fa-times mr-1" /> Hủy bỏ
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={() => window.open(`/public/asset/${itemId}`, '_blank')}
                                                theme="info"
                                            >
                                                <i className="fas fa-share-alt mr-1" /> Xem QR công khai
                                            </Button>
                                        </div>
                                    </TabPanel>

                                    {/* Tab 2: Hình ảnh & Tài liệu */}
                                    <TabPanel>
                                        <Row className="mt-4">
                                            <Col md={6}>
                                                <Form.Group as={Row} className="mb-3">
                                                    <Form.Label className="col-sm-3 col-form-label">
                                                        Ảnh tài sản
                                                    </Form.Label>
                                                    <Col sm="9">
                                                        <InputFile
                                                            enableReinitialize={true}
                                                            multiple={false}
                                                            accept={acceptImage}
                                                            labelImage="Tải ảnh lên (Max 2MB)"
                                                            values={(values.imageThumbnail && values.imageThumbnail !== 'null') ? [values.imageThumbnail] : ((values.imagePath && values.imagePath !== 'null') ? [values.imagePath] : [])}
                                                            onChange={(files) => {
                                                                if (files.length > 0) {
                                                                    setFieldValue('imagePath', files[0].filePath || files[0].url, false);
                                                                    setFieldValue('imageThumbnail', files[0].thumbPath || files[0].url, false);
                                                                } else {
                                                                    setFieldValue('imagePath', '', false);
                                                                    setFieldValue('imageThumbnail', '', false);
                                                                }
                                                            }}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group as={Row} className="mb-3">
                                                    <Form.Label className="col-sm-3 col-form-label">
                                                        Tài liệu kèm theo
                                                    </Form.Label>
                                                    <Col sm="9">
                                                        <InputFile
                                                            multiple
                                                            isDocument={true}
                                                            name="documentLink"
                                                            accept={acceptDoc}
                                                            values={values.documentLink || []}
                                                            onChange={(files) => {
                                                                setFieldValue(
                                                                    'documentLink',
                                                                    files.map(f => f.filePath || f.url).filter(Boolean),
                                                                    true
                                                                );
                                                            }}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </TabPanel>

                                {/* Các Tab khác nằm trong Form */}
                                {/* Tab 3: Danh sách phụ kiện */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <AccessoryTab assetId={itemId} />
                                    </div>
                                </TabPanel>
                                {/* Tab 4: Lịch sử điều chuyển */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <AccessoryHistoryTab assetId={itemId} />
                                    </div>
                                </TabPanel>

                                {/* Tab 5: Lịch sử sửa chữa */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <MaintenanceEventTab 
                                            assetId={itemId} 
                                            assetStatusId={values.assetStatusId}
                                            assetStatuses={assetStatuses.data?.data || []}
                                        />
                                    </div>
                                </TabPanel>
                            </Tabs>
                            </Form>
                        )}
                    </Formik>
                )}
            </Card.Body>
        </Card>
    );
};

export default AssetEdit;
